import qs from 'qs';
import type {
    RemoveItemFromWishListApiResponse,
    RemoveItemsFromWishListApiRequest,
    WishListApiResponse,
    WishListRequest
} from 'tradera/state/services/types/webapi-member-generated';
import { isomorphicBaseQueryForWishListableItems } from './utils/webapi-base-query';
import {
    createApi,
    defaultSerializeQueryArgs
} from '@reduxjs/toolkit/query/react';
import { isServer } from 'tradera/utils/nextjs';
import {
    selectAutomaticTranslationPreferred,
    selectPreferredLanguageCode
} from '../language/selectors';
import { useAppSelector } from '../hooks';
import { selectShippingRegionCountryCodeIso2 } from '../shipping-region/selectors';
import { GtmService } from 'tradera/services/google-tagmanager-service';
import { wishlistRemoveRange } from '../items/reducer';

export enum Tags {
    WISH_LIST = 'WISH_LIST'
}

/**
 * Creates a query params object for RTK Query.
 * The query parameters for RTK Query is used as key for caching hence
 * must always be the same for the same query on server and client.
 */
export const getDefaultWishListParams = ({
    page = 1,
    pageSize = 50,
    statusFilter = 'all',
    languageCodeIso2 = 'sv',
    shippingCountryCodeIso2 = 'SE',
    automaticTranslationPreferred = true
}: WishListRequest): WishListRequest => {
    return {
        page,
        pageSize,
        statusFilter,
        languageCodeIso2,
        shippingCountryCodeIso2,
        automaticTranslationPreferred
    };
};

export const useWishListParams = ({
    page,
    pageSize,
    statusFilter
}: Pick<WishListRequest, 'page' | 'pageSize' | 'statusFilter'>) => {
    const languageCodeIso2 = useAppSelector(selectPreferredLanguageCode);
    const shippingCountryCodeIso2 = useAppSelector(
        selectShippingRegionCountryCodeIso2
    );
    const automaticTranslationPreferred = useAppSelector(
        selectAutomaticTranslationPreferred
    );
    return getDefaultWishListParams({
        page,
        pageSize,
        statusFilter,
        languageCodeIso2,
        shippingCountryCodeIso2,
        automaticTranslationPreferred
    });
};

export const wishListApi = createApi({
    reducerPath: 'wishListApi',
    baseQuery: isomorphicBaseQueryForWishListableItems,
    tagTypes: Object.keys(Tags),
    endpoints: (builder) => ({
        getWishList: builder.query<WishListApiResponse, WishListRequest>({
            query: (params) => {
                const queryString = qs.stringify(params);
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/member/web/wish-list?${queryString}`;
            },
            providesTags: [Tags.WISH_LIST],
            extraOptions: {
                getWishListableItems: (response: WishListApiResponse) =>
                    response.items.map((x) => ({
                        itemId: x.itemId,
                        inWishList: true
                    }))
            }
        }),
        deleteWishListItems: builder.mutation<
            RemoveItemFromWishListApiResponse,
            RemoveItemsFromWishListApiRequest
        >({
            invalidatesTags: [Tags.WISH_LIST],
            query: (params) => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return {
                    url: `${baseUrl}/member/wishlist`,
                    method: 'DELETE',
                    body: params
                };
            },
            onQueryStarted: async (params, { dispatch }) => {
                if (params.itemIds && params.itemIds.length > 0) {
                    GtmService.trackAction(
                        'Wish list',
                        'Batch select',
                        'Delete'
                    );
                    dispatch(wishlistRemoveRange(params.itemIds));
                }
            }
        }),
        getWishListEndless: builder.query<
            WishListApiResponse & { page: number },
            WishListRequest
        >({
            query: (params) => {
                const queryString = qs.stringify(params);
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/member/web/wish-list?${queryString}`;
            },
            transformResponse: (response: WishListApiResponse, _, request) => {
                return { ...response, page: request.page ?? 1 };
            },
            providesTags: [Tags.WISH_LIST],
            serializeQueryArgs: ({
                queryArgs: { page, ...otherQueryArgs },
                endpointDefinition,
                endpointName
            }) => {
                // Exclude pageNumber from queryArgs to be ablue to use same cache for infiniteScroll
                return defaultSerializeQueryArgs({
                    endpointName,
                    queryArgs: otherQueryArgs,
                    endpointDefinition
                });
            },
            forceRefetch({ currentArg, previousArg }) {
                return currentArg?.page !== previousArg?.page;
            },
            merge: (currentCacheData, response) => {
                return {
                    ...currentCacheData,
                    items: [
                        ...(currentCacheData.items ?? []),
                        ...(response.items ?? [])
                    ],
                    totalCount: response.totalCount,
                    page: response.page
                };
            },
            extraOptions: {
                getWishListableItems: (response: WishListApiResponse) =>
                    response.items.map((x) => ({
                        itemId: x.itemId,
                        inWishList: true
                    }))
            }
        })
    })
});

export const {
    useGetWishListQuery,
    useGetWishListEndlessQuery,
    useDeleteWishListItemsMutation,
    endpoints,
    util
} = wishListApi;
